import { createFileRoute, useNavigate } from "@tanstack/react-router";

export const Route = createFileRoute("/admin/_layout/leave/balances")({
  component: LeaveBalancesDataTable,
});

import { useState, useEffect, useMemo, useCallback } from "react";
import { toast } from "sonner";
import { Controller, useForm } from "react-hook-form";

import { SidebarInset } from "@/components/ui/sidebar";
import { DashboardHeader } from "@/components/dashboard-header";

import { PaginationComponent } from "@/components/crud/Pagination/Pagination";
import { PAGE_SIZE_OPTIONS } from "@/lib/constants";
import { usePaginationState } from "@/hooks/ui/usePaginationState";

// import { SelectFilter } from "@/components/crud/filters/SelectFilter";

import dayjs from "dayjs";
import type {
  BulkAllocateLeaveBalancePayload,
  EmployeeBalance,
} from "@/types/leave";
import useFetchLeaveBalances, {
  type LeaveBalanceOrdering,
} from "@/hooks/api/leave/user-leave-balances/useFetchLeaveBalances";
import { LeaveBalancesTable } from "@/components/crud/table/LeaveBalancesTable";
import { LeaveBalancesFormSheet } from "@/components/crud/forms/LeaveBalancesFormSheet";
import useAutoCompleteUser from "@/hooks/api/user/useAutoCompleteUser";
import useUpdateLeaveBalance from "@/hooks/api/leave/user-leave-balances/useUpdateLeaveBalance";
import useBulkAllocateLeaveBalance from "@/hooks/api/leave/user-leave-balances/useBulkAllocateLeaveBalance";
import { LeaveBalancesHistorySheet } from "@/components/crud/forms/LeaveBalanceHistorySheet";

import { DateSelector } from "@/components/crud/filters/DateSelector";
import type { DateRange } from "react-day-picker";
import PageHeader from "@/components/reusable-components/header/page-header";
import FiltersPanel from "@/components/reusable-components/header/filter-pannel";
import SelectUser from "@/components/reusable-components/select-user";
import { useDebounce } from "@/hooks/use-debounce";
import type { SortDirection } from "@/components/reusable-components/sort-arrow";
import { useDataTableOrdering } from "@/hooks/ui/useDataTableOrdering";
import { useMyPermissions } from "@/hooks/api/authorization/useAuthorization";
import { can } from "@/lib/permissions";

function LeaveBalancesDataTable() {
  const { data: capabilities } = useMyPermissions();
  const canManageBalances = can(capabilities, "leave.balance.manage");
  const navigate = useNavigate();

  const [autoCompleteSearch, setAutoCompleteSearch] = useState("");
  const autoCompleteQuery = useAutoCompleteUser({
    search: autoCompleteSearch || undefined,
  });
  const users = useMemo(
    () => autoCompleteQuery.data?.pages.flatMap((p) => p.results) ?? [],
    [autoCompleteQuery.data]
  );
  const autoCompleteLoading = autoCompleteQuery.isFetching;

  const { page, setPage, pageSize, handlePageSizeChange } =
    usePaginationState();

  // Filters and Search
  const { ordering, toggleOrdering, getApiOrdering, setOrdering } =
    useDataTableOrdering<
      {
        balance: SortDirection;
        usable_balance: SortDirection;
        created_at: SortDirection;
        updated_at: SortDirection;
      },
      LeaveBalanceOrdering
    >({
      balance: null,
      usable_balance: null,
      created_at: null,
      updated_at: null,
    });

  const [startDateFilter, setStartDateFilter] = useState<string | undefined>();
  const [endDateFilter, setEndDateFilter] = useState<string | undefined>();

  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, resetDebounce] = useDebounce(searchQuery, 500);

  // React Hook Form for user filter
  const { setValue, watch, control } = useForm<{ user_id?: number }>({
    defaultValues: { user_id: undefined },
  });

  const watchedUserId = watch("user_id");

  //autoCompleteUsers code
  const handleSearchUsers = useCallback((query: string) => {
    setAutoCompleteSearch(query);
  }, []);
  // UI state
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [editingBalance, setEditingBalance] = useState<EmployeeBalance | null>(
    null
  );
  const [editingBalanceId, setEditingBalanceId] = useState<number | null>(null);
  const [isBalanceHistorySheetOpen, setIsBalanceHistorySheetOpen] =
    useState(false);
  const [currentBalance, setCurrentBalance] = useState<EmployeeBalance | null>(
    null
  );

  // Fetch hooks
  const balancesQuery = useFetchLeaveBalances({
    page_size: pageSize,
    page,
    search: debouncedSearch || undefined,
    ordering: getApiOrdering(),
    start_date: startDateFilter
      ? dayjs(startDateFilter).format("YYYY-MM-DD")
      : undefined,
    end_date: endDateFilter
      ? dayjs(endDateFilter).format("YYYY-MM-DD")
      : undefined,
  });
  const balances = balancesQuery.data?.results ?? [];
  const count = balancesQuery.data?.count ?? 0;

  const updateLeaveBalance = useUpdateLeaveBalance();
  const bulkAllocateLeaveBalance = useBulkAllocateLeaveBalance();

  const isMutating =
    updateLeaveBalance.isPending || bulkAllocateLeaveBalance.isPending;
  const isLoading = balancesQuery.isPending || isMutating;
  const isFetching = balancesQuery.isFetching || isMutating;

  const resetFilters = () => {
    setDateRange(undefined);
    setOrdering({
      balance: null,
      usable_balance: null,
      created_at: null,
      updated_at: null,
    });
    setStartDateFilter(undefined);
    setEndDateFilter(undefined);
    setSearchQuery("");
    resetDebounce();
    setValue("user_id", undefined);
    setPage(1);
  };

  // Back to page 1 whenever a filter changes
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, ordering, startDateFilter, endDateFilter, setPage]);

  // Handlers
  const handleCreate = () => {
    setEditingBalance(null);
    setEditingBalanceId(null);
    setIsViewOnly(false);
    setIsSheetOpen(true);
  };

  const handleEdit = (leave: EmployeeBalance) => {
    setEditingBalanceId(leave.id);
    setEditingBalance({ ...leave });
    setIsViewOnly(false);
    setIsSheetOpen(true);
  };

  const handleView = (balance: EmployeeBalance) => {
    setEditingBalance({ ...balance });
    setEditingBalanceId(balance.id);
    setIsViewOnly(true);
    setIsSheetOpen(true);
  };
  const handleViewHistory = (leave: EmployeeBalance) => {
    setCurrentBalance(leave);
    setIsBalanceHistorySheetOpen(true);
  };

  const handleDeleteClick = () => {
    // setBalanceToDelete(leave);
    // setIsDeleteDialogOpen(true);
  };

  const handleFormSubmit = async (data: BulkAllocateLeaveBalancePayload) => {
    if (editingBalanceId) {
      await updateLeaveBalance.mutateAsync({
        user_id: data.user_ids[0],
        leave_id: data.leave_id,
        amount: data.amount,
        remarks: data.remarks,
      });
      toast.success("Leave balance updated successfully!");
    } else {
      const result = await bulkAllocateLeaveBalance.mutateAsync(data);
      if (result.errors?.length > 0) {
        toast.warning(
          `Assigned to ${result.success} of ${result.total} users. ${result.errors.length} failed.`
        );
      } else {
        toast.success(
          `Leave balance assigned to ${result.success} user${result.success !== 1 ? "s" : ""} successfully!`
        );
      }
    }
    setIsSheetOpen(false);
  };

  const totalPages = useMemo(() => {
    return count ? Math.ceil(count / pageSize) : 1;
  }, [count, pageSize]);

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
    }
  };

  // DATE RANGE
  const [dateRange, setDateRange] = useState<DateRange | undefined>();
  useEffect(() => {
    setStartDateFilter(
      dateRange?.from ? dayjs(dateRange.from).format("YYYY-MM-DD") : undefined
    );
    setEndDateFilter(
      dateRange?.to ? dayjs(dateRange.to).format("YYYY-MM-DD") : undefined
    );
  }, [dateRange]);

  const [showFilters, setShowFilters] = useState(false);

  const [isViewOnly, setIsViewOnly] = useState(false);

  return (
    <SidebarInset>
      <DashboardHeader />

      <div className="space-y-4 p-6 pt-0">
        <PageHeader
          title="Leave Balances"
          description="Manage, review, and update leave balances for all employees."
          onToggleFilter={() => setShowFilters((prev) => !prev)}
          onAdd={canManageBalances ? handleCreate : undefined}
          addLabel="Assign Balance"
          isLoading={isFetching}
        />

        <FiltersPanel
          show={showFilters}
          onClear={resetFilters}
          isLoading={isFetching}
        >
          {/* User Filter with Autocomplete */}
          <Controller
            name="user_id"
            control={control}
            render={() => (
              <SelectUser
                watchedUserId={watchedUserId}
                users={users}
                onSearchUsers={handleSearchUsers}
                searchQuery={searchQuery}
                setSearchQuery={setSearchQuery}
                setValue={setValue}
                isLoading={autoCompleteLoading}
                fetchNextPage={autoCompleteQuery.fetchNextPage}
                hasMore={autoCompleteQuery.hasNextPage}
              />
            )}
          />

          <DateSelector
            value={dateRange}
            onApply={(range) => setDateRange(range)}
            onClear={() => setDateRange(undefined)}
          />
        </FiltersPanel>

        <LeaveBalancesTable
          balances={balances}
          onEdit={handleEdit}
          onDelete={handleDeleteClick}
          onView={handleView}
          isLoading={isLoading}
          onViewHistory={handleViewHistory}
          ordering={ordering}
          onOrderingChange={toggleOrdering}
          canManage={canManageBalances}
          onRowClick={(row) =>
            navigate({
              to: "/admin/leave/balances/$userId",
              params: { userId: String(row.user.id) },
            })
          }
        />

        <PaginationComponent
          count={count}
          page={page}
          onPageChange={handlePageChange}
          isLoading={isFetching}
          pageSize={pageSize}
          onPageSizeChange={handlePageSizeChange}
          pageSizeOptions={PAGE_SIZE_OPTIONS}
        />

        {/* isPaidLeaveOnly: balance can only be allocated to paid leave types
            (the backend rejects unpaid). */}
        <LeaveBalancesFormSheet
          isOpen={isSheetOpen}
          onClose={() => setIsSheetOpen(false)}
          onSubmit={handleFormSubmit}
          isPaidLeaveOnly
          editingBalance={editingBalance}
          isLoading={isMutating}
          isViewOnly={isViewOnly}
        />
        <LeaveBalancesHistorySheet
          isOpen={isBalanceHistorySheetOpen}
          onClose={() => setIsBalanceHistorySheetOpen(false)}
          balance={currentBalance}
        />
      </div>
    </SidebarInset>
  );
}
